import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { HouseholdDetail, HouseholdInvite, HouseholdSettingsRequestInput } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { serverNow } from "@/shared/time/clock";
import { householdLabel, useCurrentHousehold, useHouseholdLabels } from "@/shared/session/householdContext";
import { Card, EmptyState, ErrorState, LoadingState, Page, PetAvatar, TopBar } from "@/shared/ui";
import { MembersCard } from "./MembersCard";
import "./household.css";

/**
 * 出错原因给玩家看的一句（2026-09-24 巡检 P1 追加）：接口错误用 ApiError.playerMessage（还没开放、后端没有这条路的换成人话，
 * 其余照用后端写给玩家的原话）；不是接口错误的（前端自己的异常）不显示异常原文。
 */
export function failure(error: unknown): string {
  return isApiError(error) ? error.playerMessage : "操作暂时没有完成，请重试。";
}

/** 演示模式里本来就没有这一项（演示服务答“能力未接入”）：明说演示里没有，不当成出错。live 的“能力未接入”照旧走统一错误态。 */
function demoGap(error: unknown): boolean { return env.dataMode === "fixture" && isApiError(error) && error.isCapabilityUnavailable; }

// 原来这里有一张“TA 的生活照片”照片许可卡（读写家庭设置 generated_photos）。用户 2026-09-30 定：生成照片、视频这类总开关由平台在运营后台控制，
// 是宠物自发的行为，不是主人的选择——这张卡连同它的保存、核对逻辑一起拿掉；家庭设置接口本身不动（契约与后端另行配套）。

/** 家里的三条约定（家庭设置）：开关上的名字和一句说明；共同照顾者只看开着还是关着。 */
const AGREEMENTS: Array<{ key: "caregivers_can_spend" | "pet_messages" | "public_posts"; label: string; note: string }> = [
  { key: "caregivers_can_spend", label: "共同照顾者可以使用 TA 的星球账户", note: "比如帮 TA 付出发的旅费。" },
  { key: "pet_messages", label: "把 TA 的生活新鲜事发到家庭群", note: "TA 会主动把生活里的事讲给家里人听。" },
  { key: "public_posts", label: "允许 TA 的到访故事（含途中照片）出现在公开朋友圈", note: "只在 TA 真实到访之后才会发。" },
];

/** 只发点的那一项（不带别的字段，更不带 generated_photos）。 */
function agreementPatch(key: (typeof AGREEMENTS)[number]["key"], value: boolean): HouseholdSettingsRequestInput {
  if (key === "caregivers_can_spend") return { caregivers_can_spend: value };
  if (key === "pet_messages") return { pet_messages: value };
  return { public_posts: value };
}

/** 邀请还有多久有效：到期时刻写到分钟（不写秒）；已经过了写“已过期”。 */
export function inviteExpiry(expiresAt: string, now: number): string {
  const at = new Date(expiresAt);
  if (Number.isNaN(at.getTime())) return "待加入";
  if (at.getTime() <= now) return "已过期";
  const hh = String(at.getHours()).padStart(2, "0");
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `${at.getMonth() + 1}月${at.getDate()}日 ${hh}:${mm} 前有效`;
}

/** 称呼平时写成一句话（读的是保存过的那份，不是正在改的草稿）。 */
function relationshipLine(petName: string, title: string | null | undefined, label: string | null | undefined): string {
  const t = title?.trim();
  const l = label?.trim();
  if (!t && !l) return `${petName}还不知道该怎么叫你。`;
  return [t ? `${petName}叫你“${t}”` : null, l ? `你们是${l}` : null].filter(Boolean).join("，") + "。";
}

/*
 * 我们的家（二级页“不像表单”，用户 10-02“不要像表单”、c84a D0420-4；6c2b 10-03）：
 * - 称呼、家名平时就是一句话，点“改称呼”“改家名”（还没有时是“告诉 TA 怎么叫你”“起个家名”）才出输入框，改完或“不改了”收回去；
 * - 三条约定是整行开关（读屏读作开关，名字只读粗体那一句、说明挂在描述上）；共同照顾者看到的是“开着 / 关着”，不是一句“只能看”；
 * - 邀请先是一个“邀请一位家人”，点了才出称呼提示和“创建 72 小时邀请链接”；待加入的邀请写到期时刻到分钟、撤销是轻按钮。
 * 接口、请求体、缓存失效都和原来一样，只换了样子。
 */
function FamilyContent({ detail, invites, reload }: { detail: HouseholdDetail; invites: HouseholdInvite[]; reload: () => void }) {
  const { households } = useServices();
  const { userId, pet } = useCurrentHousehold();
  const labels = useHouseholdLabels(userId);
  const queryClient = useQueryClient();
  const householdId = detail.household.household_id;
  // 起了名用详情里的名字（改名后最先拿到新值）；没起名与切换栏同一套说法：“{第一只已入住宠物}的家”，撞名带序号，还没有已入住的才叫“家庭 N”。
  const title = detail.household.name?.trim() || labels?.get(householdId) || householdLabel(detail.household, 0);
  const canManage = detail.your_permissions.includes("manage");
  const petName = pet?.name ?? "TA";
  const [name, setName] = useState(detail.settings.name ?? "");
  const [editingName, setEditingName] = useState(false);
  const [relationHint, setRelationHint] = useState("");
  const [inviting, setInviting] = useState(false);
  const [newLink, setNewLink] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [ownerTitle, setOwnerTitle] = useState("");
  const [relationLabel, setRelationLabel] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const relationship = useQuery({ queryKey: queryKeys.householdRelationship(userId ?? "-", pet?.pet_id ?? "-"), queryFn: () => households.relationship(pet!.pet_id), enabled: Boolean(userId && pet) });
  useEffect(() => { if (relationship.data) { setOwnerTitle(relationship.data.owner_title ?? ""); setRelationLabel(relationship.data.relation_label ?? ""); } }, [relationship.data]);
  useEffect(() => setName(detail.settings.name ?? ""), [detail.settings.name]);
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: queryKeys.householdDetail(userId ?? "-", householdId) }); void queryClient.invalidateQueries({ queryKey: queryKeys.householdInvites(userId ?? "-", householdId) }); void queryClient.invalidateQueries({ queryKey: queryKeys.households(userId ?? "-") }); reload(); };
  const settings = useMutation({ mutationFn: (patch: HouseholdSettingsRequestInput) => households.updateSettings(householdId, patch), onSuccess: (updated) => { queryClient.setQueryData(queryKeys.householdDetail(userId ?? "-", householdId), updated); refresh(); } });
  // 称呼与“TA 的档案”（/me/dna）里的 owner_title 是同一份（后端同一张 web_pet_relationships）：保存后两边的缓存一起失效。
  const saveRelationship = useMutation({ mutationFn: () => households.saveRelationship(pet!.pet_id, { owner_title: ownerTitle.trim() || null, relation_label: relationLabel.trim() || null }), onSuccess: () => { setEditingTitle(false); void queryClient.invalidateQueries({ queryKey: queryKeys.householdRelationship(userId ?? "-", pet?.pet_id ?? "-") }); void queryClient.invalidateQueries({ queryKey: queryKeys.dnaFor(userId ?? "-", pet?.pet_id ?? "-") }); } });
  const createInvite = useMutation({ mutationFn: () => households.createInvite(householdId, relationHint.trim() || null), onSuccess: (created) => { setNewLink(`${window.location.origin}${created.join_path}`); setInviting(false); setRelationHint(""); refresh(); } });
  const revoke = useMutation({ mutationFn: (id: string) => households.revokeInvite(householdId, id), onSuccess: refresh });
  const named = Boolean(detail.settings.name?.trim());
  const cancelTitle = () => { setOwnerTitle(relationship.data?.owner_title ?? ""); setRelationLabel(relationship.data?.relation_label ?? ""); saveRelationship.reset(); setEditingTitle(false); };
  const cancelName = () => { setName(detail.settings.name ?? ""); settings.reset(); setEditingName(false); };
  const hasTitle = Boolean(relationship.data?.owner_title?.trim() || relationship.data?.relation_label?.trim());
  const now = serverNow();
  return <div className="ps-family-content">
    <section className="ps-family-hero"><span>家庭档案</span><h2>{title}</h2><p>{detail.household.pets.length} 位伙伴 · {detail.members.length} 位家人</p></section>
    <Card paper className="ps-family-card"><h2>住在这里</h2><div className="ps-family-pets">{detail.household.pets.map((memberPet) => <div key={memberPet.pet_id}><PetAvatar petId={memberPet.pet_id} name={memberPet.name} species={memberPet.species} photoUrl={memberPet.photo_url} origin={memberPet.origin} surface="paper" size={42} /><span>{memberPet.name}</span></div>)}</div><Link to="/home">回家看看 →</Link></Card>
    <MembersCard detail={detail} userId={userId} />
    <Card paper className="ps-family-card">
      <h2>{petName} 怎么称呼你</h2>
      {relationship.isPending ? <LoadingState label="正在读取你们的称呼…" /> : relationship.isError ? <ErrorState error={relationship.error} onRetry={() => void relationship.refetch()} /> : editingTitle ? (
        <form className="ps-family-edit" onSubmit={(event) => { event.preventDefault(); saveRelationship.mutate(); }}>
          <label>TA 对你的称呼<input value={ownerTitle} onChange={(event) => setOwnerTitle(event.target.value)} maxLength={12} placeholder="例如：妈妈" /></label>
          <label>你们的关系<input value={relationLabel} onChange={(event) => setRelationLabel(event.target.value)} maxLength={12} placeholder="例如：家人" /></label>
          <div className="ps-family-edit__actions">
            <button type="submit" disabled={saveRelationship.isPending}>{saveRelationship.isPending ? "保存中…" : "保存称呼"}</button>
            <button type="button" className="is-quiet" disabled={saveRelationship.isPending} onClick={cancelTitle}>不改了</button>
          </div>
          {saveRelationship.isError ? <p role="alert">{failure(saveRelationship.error)}</p> : null}
        </form>
      ) : (
        <>
          <p className="ps-family-say">{relationshipLine(petName, relationship.data?.owner_title, relationship.data?.relation_label)}</p>
          <button type="button" className="is-quiet" onClick={() => { saveRelationship.reset(); setEditingTitle(true); }}>{hasTitle ? "改称呼" : "告诉 TA 怎么叫你"}</button>
          {saveRelationship.isSuccess ? <p role="status">称呼已保存，只用于你与 TA 的关系。</p> : null}
        </>
      )}
    </Card>
    <Card paper className="ps-family-card">
      <h2>家里的约定</h2>
      {canManage && editingName ? (
        <form className="ps-family-edit" onSubmit={(event) => { event.preventDefault(); settings.mutate({ name: name.trim() || null }, { onSuccess: () => setEditingName(false) }); }}>
          <label>家名<input value={name} onChange={(event) => setName(event.target.value)} maxLength={48} placeholder={title} /></label>
          <div className="ps-family-edit__actions">
            <button type="submit" disabled={settings.isPending || name === (detail.settings.name ?? "")}>保存家名</button>
            <button type="button" className="is-quiet" disabled={settings.isPending} onClick={cancelName}>不改了</button>
          </div>
        </form>
      ) : (
        <div className="ps-family-row ps-family-name">
          <div><strong>{named ? detail.settings.name!.trim() : "还没起家名"}</strong><small>{named ? "家名" : `现在大家叫它“${title}”`}</small></div>
          {canManage ? <button type="button" className="is-quiet" onClick={() => { settings.reset(); setEditingName(true); }}>{named ? "改家名" : "起个家名"}</button> : null}
        </div>
      )}
      <div className="ps-family-switches">
        {AGREEMENTS.map((agreement) => {
          const on = detail.settings[agreement.key];
          const labelId = `family-agreement-${agreement.key}`;
          return canManage ? (
            <label key={agreement.key} className="ps-family-switch">
              <span><strong id={labelId}>{agreement.label}</strong><small id={`${labelId}-note`}>{agreement.note}</small></span>
              <input type="checkbox" role="switch" aria-labelledby={labelId} aria-describedby={`${labelId}-note`} checked={on} disabled={settings.isPending} onChange={(event) => settings.mutate(agreementPatch(agreement.key, event.target.checked))} />
            </label>
          ) : (
            <div key={agreement.key} className="ps-family-switch is-readonly">
              <span><strong>{agreement.label}</strong><small>{agreement.note}</small></span>
              <em className={on ? "is-on" : undefined}>{on ? "开着" : "关着"}</em>
            </div>
          );
        })}
      </div>
      {settings.isError ? <p role="alert">{failure(settings.error)}</p> : null}
      {canManage ? null : <p className="ps-family-fine">只有家庭管理员能改家里的约定。</p>}
    </Card>
    <Card paper className="ps-family-card">
      <h2>邀请家人</h2>
      {canManage ? <>
        <p>邀请只授予共同照顾者角色。链接只显示这一次，请自己妥善转交。</p>
        {inviting ? (
          <form className="ps-family-edit" onSubmit={(event) => { event.preventDefault(); createInvite.mutate(); }}>
            <label>给家人的称呼提示<input value={relationHint} onChange={(event) => setRelationHint(event.target.value)} placeholder="例如：爸爸（不影响权限）" maxLength={12} /></label>
            <div className="ps-family-edit__actions">
              <button type="submit" disabled={createInvite.isPending}>{createInvite.isPending ? "正在创建…" : "创建 72 小时邀请链接"}</button>
              <button type="button" className="is-quiet" disabled={createInvite.isPending} onClick={() => { setInviting(false); setRelationHint(""); createInvite.reset(); }}>先不</button>
            </div>
          </form>
        ) : <button type="button" onClick={() => { createInvite.reset(); setInviting(true); }}>邀请一位家人</button>}
        {newLink ? <div className="ps-family-created"><label>这次创建的专属链接<input readOnly value={newLink} onFocus={(event) => event.target.select()} /></label><button type="button" onClick={() => { void navigator.clipboard.writeText(newLink).then(() => setCopyMessage("已复制，请只发给受邀家人。"), () => setCopyMessage("复制失败，请选中上方链接手动复制。")); }}>复制链接</button>{copyMessage ? <p role="status">{copyMessage}</p> : null}</div> : null}
        {createInvite.isError ? <p role="alert">{failure(createInvite.error)}</p> : null}
        {invites.filter((invite) => invite.status === "pending").map((invite) => <div className="ps-family-row" key={invite.invite_id}><div><strong>{invite.relation_hint || "一位家人"}</strong><small>待加入 · {inviteExpiry(invite.expires_at, now)}</small></div><button type="button" className="is-quiet" disabled={revoke.isPending} onClick={() => { if (window.confirm("撤销这份邀请？原链接将不能再使用。")) revoke.mutate(invite.invite_id); }}>撤销</button></div>)}
        {revoke.isError ? <p role="alert">{failure(revoke.error)}</p> : null}
      </> : <p>请让家庭管理员发出邀请。</p>}
    </Card>
  </div>;
}

export function HouseholdPage() {
  const { households } = useServices();
  const { userId, household } = useCurrentHousehold();
  const householdId = household?.household_id ?? "";
  // 演示模式没有账号与家庭（HouseholdProvider 给的是 null）：照 useActiveHome 的写法，演示下不因 userId 为空而停用（键的账号位是 "-"），
  // 照样问演示服务——有数据就显示，答“能力未接入”就明说演示里没有；不再误说“还没有家庭”。live 的键与启用条件一点不变。
  const fixture = env.dataMode === "fixture";
  const detail = useQuery({ queryKey: queryKeys.householdDetail(userId ?? "-", householdId), queryFn: ({ signal }) => households.detail(householdId, signal), enabled: fixture || Boolean(userId && householdId) });
  const invites = useQuery({ queryKey: queryKeys.householdInvites(userId ?? "-", householdId), queryFn: () => households.invites(householdId), enabled: Boolean(userId && householdId && detail.data?.your_permissions.includes("manage")) });
  return <Page className="ps-family-page"><TopBar title="我们的家" subtitle="同一个家，彼此有各自的角色" back="/me" />{!fixture && !householdId ? <EmptyState icon="home" title="还没有家庭">先让伙伴入住，家会出现在这里。</EmptyState> : detail.isPending ? <LoadingState label="正在翻开家庭档案…" /> : demoGap(detail.error) ? <EmptyState icon="home" title="演示模式没有真实的家庭资料">住进来以后，家人、邀请和约定都在这里。</EmptyState> : detail.isError ? <ErrorState error={detail.error} onRetry={() => void detail.refetch()} /> : detail.data ? <FamilyContent key={`${userId}:${householdId}`} detail={detail.data} invites={invites.data ?? []} reload={() => void detail.refetch()} /> : null}</Page>;
}
